// src/Components/Communities/CommunityDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../rbac/AuthContext";
import { db } from "../../firebase";
import {
  doc, onSnapshot, getDoc, updateDoc, arrayUnion, serverTimestamp,
  query, where, writeBatch, arrayRemove, collection
} from "firebase/firestore";
import {
  Container, Card, Spinner, Alert, Nav, Button, Table, Form, Modal
} from "react-bootstrap";
import AddBlock from "../Manager/AddBlock";
import MembersPanel from "../Manager/MembersPanel";
import CreateEvent from "../events/CreateEvent";

const formatTs = (ts) => {
  try {
    return ts?.toDate?.()?.toLocaleString?.() || "—";
  } catch {
    return "—";
  }
};

const makeId = (prefix = "ID") =>
  `${prefix}-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

export default function CommunityDashboard() {
  const { id: adminCommunityId, communityId: managerResidentCommunityId } = useParams();
  const communityParamId = adminCommunityId || managerResidentCommunityId;

  const { currentUser, role, isAdmin, isManager, isResident } = useAuth();

  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Sidebar section state
  const [section, setSection] = useState("dashboard"); // dashboard | events | members | blocks

  // Forms
  const [newMemberUid, setNewMemberUid] = useState("");
  const [eventForm, setEventForm] = useState({ title: "", date: "", description: "" });

  // 🔎 Members search (UI-only; passed to MembersPanel if it supports it)
  const [memberSearch, setMemberSearch] = useState("");

  // Events state (top-level events collection)
  const [eventsDocs, setEventsDocs] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState("");
  const navigate = useNavigate();

  // --- Role change modal state ---
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleModalLoading, setRoleModalLoading] = useState(false);
  const [roleModalError, setRoleModalError] = useState("");
  const [targetUser, setTargetUser] = useState(null); // { uid, displayName, email, role }
  const [newRole, setNewRole] = useState("resident");

  // Load events by communityId
  useEffect(() => {
    if (!community?.id) {
      setEventsDocs([]);
      setEventsLoading(false);
      return;
    }
    setEventsLoading(true);
    setEventsError("");

    const q = query(collection(db, "events"), where("communityId", "==", community.id));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEventsDocs(rows);
        setEventsLoading(false);
      },
      (err) => {
        console.error("Events snapshot error:", err);
        setEventsError(err?.message || "Failed to load events.");
        setEventsLoading(false);
      }
    );
    return () => unsub();
  }, [community?.id]);

  // Real-time subscription to community doc
  useEffect(() => {
    if (!communityParamId) return;
    setLoading(true);
    setLoadError("");

    const ref = doc(db, "communities", communityParamId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setLoadError("Community not found.");
          setCommunity(null);
          setLoading(false);
          return;
        }
        setCommunity({ id: snap.id, ...snap.data() });
        setLoading(false);
      },
      (err) => {
        console.error("onSnapshot error:", err.code, err.message);
        setLoadError(err?.message || "Failed to load community.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [communityParamId]);

  const membersCount = useMemo(
    () => (Array.isArray(community?.members) ? community.members.length : 0),
    [community]
  );

  /** Admin/Manager-only: add/remove members */
  const addMember = async () => {
    if (!isAdmin && !isManager) return alert("Not allowed.");
    const uid = newMemberUid.trim();
    if (!uid) return alert("Enter a user UID to add.");
    try {
      const ref = doc(db, "communities", communityParamId);
      await updateDoc(ref, { members: arrayUnion(uid) });
      setNewMemberUid("");
    } catch (err) {
      console.error("addMember error:", err);
      alert(err?.message || "Failed to add member.");
    }
  };

  // Open modal for a specific user UID
  const openRoleModalFor = async (uid) => {
    if (!uid) return;
    if (!isAdmin && !isManager) {
      alert("Not allowed.");
      return;
    }
    try {
      setRoleModalError("");
      setRoleModalLoading(true);

      const ref = doc(db, "users", uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        setRoleModalError("User not found.");
        setRoleModalLoading(false);
        setShowRoleModal(true);
        return;
      }
      const data = snap.data();
      const currentRole = (data?.role || "resident").toLowerCase();

      // Manager cannot edit Admin users
      if (isManager && currentRole === "admin") {
        setRoleModalError("Managers cannot modify admin users.");
      }

      setTargetUser({
        uid,
        displayName: data?.fullName || data?.displayName || data?.email || uid,
        email: data?.email || "",
        role: currentRole,
      });

      setNewRole(currentRole);
      setShowRoleModal(true);
    } catch (err) {
      console.error("Failed to load user for role edit:", err);
      setRoleModalError(err?.message || "Failed to load user.");
      setShowRoleModal(true);
    } finally {
      setRoleModalLoading(false);
    }
  };

  const closeRoleModal = () => {
    setShowRoleModal(false);
    setRoleModalError("");
    setTargetUser(null);
    setNewRole("resident");
  };

  // Persist the role change to Firestore
  const saveRoleChange = async () => {
    if (!targetUser?.uid) return;
    if (!isAdmin && !isManager) {
      alert("Not allowed.");
      return;
    }

    const allowedRolesForManager = ["resident", "manager"];
    if (isManager) {
      if (targetUser.role === "admin") {
        setRoleModalError("Managers cannot modify admin users.");
        return;
      }
      if (!allowedRolesForManager.includes(newRole)) {
        setRoleModalError("Managers can only set 'resident' or 'manager'.");
        return;
      }
    }

    if (newRole === targetUser.role) {
      closeRoleModal();
      return;
    }

    try {
      setRoleModalLoading(true);
      await updateDoc(doc(db, "users", targetUser.uid), {
        role: newRole,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid || null,
      });
      closeRoleModal();
    } catch (err) {
      console.error("Failed to update role:", err);
      setRoleModalError(err?.message || "Failed to update role.");
    } finally {
      setRoleModalLoading(false);
    }
  };

  const removeMember = async (uid) => {
    if (!isAdmin && !isManager) return alert("Not allowed.");
    if (!window.confirm("Remove this member UID from the community?")) return;
    try {
      const ref = doc(db, "communities", communityParamId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      const data = snap.data();
      const next = (data.members || []).filter((m) => m !== uid);
      await updateDoc(ref, { members: next });
    } catch (err) {
      console.error("removeMember error:", err);
      alert(err?.message || "Failed to remove member.");
    }
  };

  /** Admin/Manager-only: delete event in top-level collection + remove references */
  const deleteEvent = async (eventId) => {
    if (!isAdmin && !isManager) return alert("Not allowed.");
    if (!window.confirm("Delete this event?")) return;
    try {
      const communityRef = doc(db, "communities", community.id);
      const eventRef = doc(db, "events", eventId);
      const batch = writeBatch(db);
      batch.delete(eventRef);
      batch.update(communityRef, { eventIds: arrayRemove(eventId) });
      batch.update(communityRef, { events: arrayRemove(eventId) });
      await batch.commit();
    } catch (err) {
      console.error("Delete event error:", err);
      alert(err?.message || "Failed to delete event.");
    }
  };

  /** Admin/Manager-only: add inline event under community.events */
  const addEvent = async () => {
    if (!isAdmin && !isManager) return alert("Not allowed.");
    const { title, date, description } = eventForm;
    if (!title || !date) {
      alert("Title and date are required.");
      return;
    }
    const entry = {
      id: makeId("EVENT"),
      title,
      date, // YYYY-MM-DD
      description: description || "",
      createdAt: serverTimestamp(),
    };
    try {
      const ref = doc(db, "communities", communityParamId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      const data = snap.data();
      const next = Array.isArray(data.events) ? [...data.events, entry] : [entry];
      await updateDoc(ref, { events: next });
      setEventForm({ title: "", date: "", description: "" });
    } catch (err) {
      console.error("addEvent error:", err);
      alert(err?.message || "Failed to add event.");
    }
  };

  const isManagerOrAdmin = isAdmin || isManager;

  // --- Mobile-only sticky, scrollable tab bar (Dashboard, Events, Members, Blocks & Flats) ---
  const MobileTabs = (
    <div className="sticky-mobile-tabs bg-white border-bottom d-md-none">
      <div className="px-2 py-2 mobile-nav-scroll">
        <Nav
          variant="pills"           // safe across React-Bootstrap versions
          className="flex-nowrap"
          activeKey={section}
          onSelect={(k) => {
            if (!k) return;
            // Only allow switching to tabs the user can access
            if (k === "dashboard" || k === "events") {
              setSection(k);
            } else if (k === "members" && isManagerOrAdmin) {
              setSection(k);
            } else if (k === "blocks" && isManagerOrAdmin) {
              setSection(k);
            }
            // Scroll to top for nicer UX on mobile
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <Nav.Item>
            <Nav.Link eventKey="dashboard" className="text-capitalize">Dashboard</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="events" className="text-capitalize">Events</Nav.Link>
          </Nav.Item>
          {isManagerOrAdmin && (
            <Nav.Item>
              <Nav.Link eventKey="members" className="text-capitalize">Members</Nav.Link>
            </Nav.Item>
          )}
          {isManagerOrAdmin && (
            <Nav.Item>
              <Nav.Link eventKey="blocks" className="text-capitalize">Blocks &amp; Flats</Nav.Link>
            </Nav.Item>
          )}
        </Nav>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Loading community…</span>
        </div>
      </Container>
    );
  }

  if (loadError || !community) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{loadError || "Community not found."}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-3 content-safe-bottom">
      {/* Inline styles for the mobile tabs (scoped to this page) */}
      <style>{`
        /* Horizontal scroll for the tab row */
        .mobile-nav-scroll {
          overflow-x: auto;
          white-space: nowrap;
          -webkit-overflow-scrolling: touch;
        }

        /* Ensure the tabs sit below your fixed header.
           You can set --app-header-height globally to match your header height.
           Fallback to 56px (Bootstrap default navbar). */
        .sticky-mobile-tabs {
          position: sticky;
          top: var(--app-header-height, 56px);
          z-index: 1020; /* Above cards/tables but below modals */
        }

        /* Compact tab look to avoid occupying excessive vertical space */
        .mobile-nav-scroll .nav-link {
          padding: .375rem .6rem;
          font-weight: 600;
          font-size: .875rem;
        }

        /* Add safe bottom space so your bottom menu doesn't overlap content on small screens. 
           You can set --app-bottom-nav-height globally (e.g., 64px).
           Fallback adds 72px padding. */
        .content-safe-bottom {
          padding-bottom: var(--app-bottom-nav-height, 72px);
        }
      `}</style>

      {/* Mobile horizontal tabs */}
      {MobileTabs}

      <div className="row mt-2 mt-md-0">
        <Card className="mb-3 ">
          <Card.Body>
             <div><strong>Community Name:</strong>  <strong className="text-primary-label">{community.name }</strong> - (<code>{community.id}</code>)</div>
           
            <div><strong>Address:</strong> {community.address || "—"}</div>
            {/* <div><strong>Description:</strong> {community.description || "—"}</div> */}
            {/* <div><strong>Created:</strong> {formatTs(community.createdAt)}</div> */}
          </Card.Body>
        </Card>

        {/* Left Sidebar — keep desktop as-is, hide on mobile */}
        <div className="col-12 col-md-3 col-lg-2 mb-3 d-none d-md-block">
          <Card>
            <Card.Header><strong>{community.name || "Community"}</strong></Card.Header>
            <Nav className="flex-column">
              <Nav.Link active={section === "dashboard"} onClick={() => setSection("dashboard")}>
                📊 Dashboard
              </Nav.Link>

              <Nav.Link active={section === "events"} onClick={() => setSection("events")}>
                📅 Events
              </Nav.Link>

              {isManagerOrAdmin && (
                <Nav.Link active={section === "members"} onClick={() => setSection("members")}>
                  👥 Members
                </Nav.Link>
              )}
              {isManagerOrAdmin && (
                <Nav.Link active={section === "blocks"} onClick={() => setSection("blocks")}>
                  🏢 Blocks & Flats
                </Nav.Link>
              )}
            </Nav>
          </Card>
        </div>

        {/* Main Content */}
        <div className="col-12 col-md-9 col-lg-10">
          {section === "dashboard" && (
            <Card>
              <Card.Header>Dashboard</Card.Header>
              <Card.Body>
                <div className="row g-3">
                  <div className="col-md-3">
                    <Card bg="light"><Card.Body>
                      <h6 className="text-muted">Members</h6>
                      <h3>{membersCount}</h3>
                    </Card.Body></Card>
                  </div>
                  <div className="col-md-3">
                    <Card bg="light"><Card.Body>
                      <h6 className="text-muted">Association</h6>
                      <h3>{community.association?.length || 0}</h3>
                    </Card.Body></Card>
                  </div>
                  <div className="col-md-3">
                    <Card bg="light"><Card.Body>
                      <h6 className="text-muted">Events</h6>
                      <h3>{community.events?.length || 0}</h3>
                    </Card.Body></Card>
                  </div>
                  <div className="col-md-3">
                    <Card bg="light"><Card.Body>
                      <h6 className="text-muted">Blocks</h6>
                      <h3>{community.blocks?.length || 0}</h3>
                    </Card.Body></Card>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          {section === "events" && (
            <Card>
              <Card.Header>Events</Card.Header>
              <Card.Body>
                {/* Allow event creation only for manager/admin */}
                {isManagerOrAdmin && <CreateEvent communityId={community.id} />}

                {eventsError && <Alert variant="danger" className="mt-3">{eventsError}</Alert>}

                {eventsLoading ? (
                  <div className="d-flex align-items-center mt-2">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading events…</span>
                  </div>
                ) : (
                  <Table bordered hover responsive size="sm" className="mt-3">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Committee</th>
                        <th style={{ width: 160 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventsDocs.length === 0 ? (
                        <tr><td colSpan={5} className="text-center">No events</td></tr>
                      ) : (
                        eventsDocs.map((ev) => (
                          <tr key={ev.id}>
                            <td>{ev.name || ev.title || "—"}</td>
                            <td>{ev.date || "—"}</td>
                            <td className="text-truncate" style={{ maxWidth: 360 }}>
                              {ev.description || "—"}
                            </td>
                            <td>
                              {Array.isArray(ev.committee)
                                ? ev.committee.length
                                : (ev.committee && typeof ev.committee === "object")
                                ? Object.keys(ev.committee).length
                                : 0}
                            </td>
                            <td className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => navigate(`/community/${community.id}/events/${ev.id}`)}
                              >
                                View
                              </Button>
                              {isManagerOrAdmin && (
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => deleteEvent(ev.id)}
                                >
                                  Delete
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}

          {section === "members" && isManagerOrAdmin && (
            <Card>
              <Card.Header className="d-none d-lg-block">Members</Card.Header>
              <Card.Body>
                {/* 🔎 Search bar for members (UI-only; pass to MembersPanel) */}
                <Form.Control
                  type="search"
                  placeholder="Search members by name, email, or UID…"
                  aria-label="Search members"
                  className="mb-3"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                />

                <MembersPanel
                  communityId={community.id}
                  canEditRole={true}
                  onEditRole={(uid) => openRoleModalFor(uid)}
                  searchQuery={memberSearch}  // safe: MembersPanel can ignore if unsupported
                />

                {/* Optional quick add by UID */}
                <hr />
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    placeholder="Enter user UID"
                    value={newMemberUid}
                    onChange={(e) => setNewMemberUid(e.target.value)}
                  />
                  <Button variant="primary" onClick={addMember}>Add</Button>
                </div>
              </Card.Body>
            </Card>
          )}

          {section === "blocks" && isManagerOrAdmin && (
            <Card>
              <Card.Header className="d-none d-lg-block">Blocks & Flats</Card.Header>
              <Card.Body>
                <AddBlock communityId={community.id} />
                {Array.isArray(community.blocks) && community.blocks.length > 0 && (
                  <>
                    <hr />
                    <h5>Saved Blocks</h5>
                    {community.blocks.map((b) => (
                      <div key={b.id} className="mb-4">
                        <strong>Block {b.name}</strong> • Floors: {b.floors} • Flats/Floor: {b.flatsPerFloor}
                        <div className="text-muted">Range: {b.startNumber} → {b.endNumber}</div>

                        <Table bordered responsive size="sm" className="mt-2">
                          <thead>
                            <tr>
                              <th>Floor</th>
                              <th>Flats</th>
                            </tr>
                          </thead>
                          <tbody>
                            {b.hasGround && b.groundFlatsCount > 0 && (
                              <tr>
                                <td>Ground ({b.groundLabel})</td>
                                <td>
                                  <div className="d-flex flex-wrap gap-2">
                                    {Array.from({ length: b.groundFlatsCount }).map((_, i) => {
                                      const idx = (b.groundIndexStart ?? 1) + i;
                                      const width = b.groundIndexWidth ?? 2;
                                      const label = `${b.name}-${b.groundLabel}${String(idx).padStart(width, "0")}`;
                                      return <code key={label}>{label}</code>;
                                    })}
                                  </div>
                                </td>
                              </tr>
                            )}
                            {Array.from({ length: b.floors }).map((_, fi) => {
                              const f = fi + 1;
                              return (
                                <tr key={`floor-${f}`}>
                                  <td>Floor {f}</td>
                                  <td>
                                    <div className="d-flex flex-wrap gap-2">
                                      {Array.from({ length: b.flatsPerFloor }).map((__, i) => {
                                        const idx = (b.upperIndexStart ?? 1) + i;
                                        const width = b.upperIndexWidth ?? 2;
                                        const label = `${b.name}-${f}${String(idx).padStart(width, "0")}`;
                                        return <code key={label}>{label}</code>;
                                      })}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    ))}
                  </>
                )}
              </Card.Body>
            </Card>
          )}
        </div>
      </div>

      <Modal show={showRoleModal} onHide={closeRoleModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {roleModalLoading ? (
            <div className="d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <span>Loading…</span>
            </div>
          ) : (
            <>
              {roleModalError && <Alert variant="warning">{roleModalError}</Alert>}

              <div className="mb-2">
                <div><strong>User:</strong> {targetUser?.displayName || "—"}</div>
                <div className="text-muted"><small>{targetUser?.email || ""}</small></div>
              </div>

              <Form.Group className="mt-2">
                <Form.Label>New Role</Form.Label>
                <Form.Select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  disabled={isManager && targetUser?.role === "admin"}
                >
                  {isAdmin && (
                    <>
                      <option value="admin">admin</option>
                      <option value="manager">manager</option>
                      <option value="resident">resident</option>
                    </>
                  )}
                  {isManager && (
                    <>
                      <option value="manager">manager</option>
                      <option value="resident">resident</option>
                    </>
                  )}
                </Form.Select>
                <div className="form-text">
                  {isManager ? "Managers can update only resident/manager roles." : "Admins can set any role."}
                </div>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeRoleModal} disabled={roleModalLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={saveRoleChange}
            disabled={roleModalLoading || (isManager && targetUser?.role === "admin")}
          >
            {roleModalLoading ? "Saving…" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
